/**
 * Live snapshot manager.
 *
 * Keeps one "current" snapshot warm so the agent loop can reuse it instead of
 * rebuilding the (expensive) augmented substrate on every step. Freshness is
 * decided by the **raw substrate itself**, not by a proxy signal:
 *
 *   - A capture is two tiers: a CHEAP raw substrate (`page.ariaSnapshot` +
 *     pre-snapshot enrichers) and an EXPENSIVE augmentation pass over it.
 *   - The raw substrate is the ground truth for "did the representation change"
 *     — it's computed by the browser's accessibility engine, so it reflects CSS
 *     visibility, shadow DOM, iframes, and layout, which a MutationObserver
 *     can't see. We compare it EXACTLY (string equality, collision-free).
 *   - On a request we always take a fresh raw substrate (cheap) and, if it's
 *     identical to the current one, reuse the already-augmented snapshot;
 *     otherwise we re-augment.
 *   - `warm()` runs that during the agent's think-time so the next request hits
 *     the reuse path.
 *
 * `__domRevision` / DOM quiescence (injected as `waitQuiet`) is only a
 * best-effort trigger for *when* to warm — never load-bearing for correctness.
 *
 * All page access is injected as `LiveSnapshotDeps`, so this unit-tests without
 * a real browser. Captures are serialized through an internal queue so two
 * never overlap (the enrichers mutate the live DOM).
 *
 * @module live-snapshot
 */

import type { BrowserLogger } from './logger.js';

/**
 * Telemetry events emitted by the manager. Best-effort, never load-bearing —
 * the client aggregates these into per-step LiveSnapshot efficacy stats.
 */
export type LiveSnapshotEvent =
  /** A fresh-augment pass ran (a freshness miss, not a reuse hit). */
  | { kind: 'mint' }
  /** An `augment()` call completed; `ms` is its wall time. */
  | { kind: 'augment'; ms: number }
  /** A background `warm` performed a fresh augment during think-time. */
  | { kind: 'warm' }
  /** A `warm` yielded to a concurrent real request instead of minting. */
  | { kind: 'warm-preempted' }
  /** A request waited `ms` behind in-flight work on the serialized chain. */
  | { kind: 'warm-block'; ms: number };

/** A frozen snapshot of the page substrate. */
export interface MintedSnapshot {
  /** Monotonic mint id, incremented per fresh raw capture. */
  generation: number;
  /** Main-frame URL at capture time. */
  url: string;
  /**
   * The raw substrate (enrichers + ariaSnapshot, pre-augmentation). This is the
   * freshness key: two captures are the same iff their `rawYaml` is identical.
   */
  rawYaml: string;
  /**
   * The augmented substrate, or null when augmentation hasn't run yet (the
   * raw-on-miss path returns the raw substrate immediately and augments async).
   */
  augmentedYaml: string | null;
  /** Wall-clock capture time (ms). */
  capturedAtMs: number;
}

/** The agent-visible YAML for a mint: augmented when available, else raw. */
export function mintedYaml(snap: MintedSnapshot): string {
  return snap.augmentedYaml ?? snap.rawYaml;
}

export interface LiveSnapshotDeps {
  /**
   * CHEAP tier: produce the raw substrate (enrichers + ariaSnapshot) and the
   * page URL. Returns null when the page is unavailable (closed / navigating /
   * aborted) — the manager then keeps the current snapshot rather than poison it.
   */
  captureRaw: (
    signal?: AbortSignal
  ) => Promise<{ rawYaml: string; url: string } | null>;
  /** EXPENSIVE tier: augment a raw substrate into the full substrate. */
  augment: (rawYaml: string, signal?: AbortSignal) => Promise<string>;
  /**
   * Wait until the DOM has been quiet for `quietMs` (bounded by `maxWaitMs`).
   * Best-effort warm trigger; absent in tests defaults to a microtask.
   */
  waitQuiet?: (quietMs: number, maxWaitMs: number) => Promise<void>;
  /** Injectable clock (defaults to Date.now). */
  now?: () => number;
  logger?: BrowserLogger;
  /** Optional telemetry sink. Best-effort; never load-bearing. */
  onEvent?: (ev: LiveSnapshotEvent) => void;
}

export interface MintOptions {
  signal?: AbortSignal;
  /** Re-augment even if the raw substrate is unchanged. */
  force?: boolean;
  /**
   * On a freshness miss, return the raw substrate immediately and augment in the
   * background (instead of blocking on augmentation). Default false → block.
   */
  rawOnMiss?: boolean;
}

export interface WarmOptions {
  quietMs?: number;
  maxWaitMs?: number;
  signal?: AbortSignal;
}

export class LiveSnapshotManager {
  private current: MintedSnapshot | null = null;
  private generationCounter = 0;
  /** Serializes captures so two never run against the page at once. */
  private chain: Promise<unknown> = Promise.resolve();
  private readonly now: () => number;
  /**
   * Aborts the quiet-wait of an in-flight `warm` so a real request can preempt
   * it (a real request will capture+augment itself; warming would duplicate).
   */
  private warmQuietAbort: AbortController | null = null;
  /** Count of real requests (mint/getReusable) currently in flight. */
  private realRequestDepth = 0;

  constructor(private readonly deps: LiveSnapshotDeps) {
    this.now = deps.now ?? (() => Date.now());
  }

  /** The current mint without re-checking the live page (may be stale). */
  peek(): MintedSnapshot | null {
    return this.current;
  }

  /** Drop the cached snapshot (e.g. on navigation / new page). */
  reset(): void {
    this.current = null;
  }

  /**
   * Run `fn` after any in-flight capture settles; never overlap captures. The
   * time spent queued behind prior work is emitted as `warm-block` — when the
   * occupant is a background `warm`'s augment, this is exactly the latency a
   * real request pays for the serialization.
   */
  private enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const enqueuedAt = this.now();
    const exec = () => {
      const waited = this.now() - enqueuedAt;
      if (waited > 0) this.deps.onEvent?.({ kind: 'warm-block', ms: waited });
      return fn();
    };
    const run = this.chain.then(exec, exec);
    this.chain = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  /**
   * Produce a current snapshot, reusing the augmented one when the raw substrate
   * is unchanged. Always takes a fresh (cheap) raw substrate first, so it never
   * serves a stale representation. Returns null when the page is unavailable on
   * the very first mint (nothing to serve) — the caller falls back to a legacy
   * capture rather than seeing an error.
   *
   * As a real-request entry point, this preempts an in-flight `warm` so the warm
   * yields to this capture instead of racing or blocking it.
   */
  mint(opts?: MintOptions): Promise<MintedSnapshot | null> {
    this.warmQuietAbort?.abort();
    return this.runReal(() => this.doMint(opts));
  }

  /** Wrap a real-request body so it preempts warms while it's in flight. */
  private runReal<T>(fn: () => Promise<T>): Promise<T> {
    this.realRequestDepth += 1;
    const run = this.enqueue(fn);
    void run.then(
      () => (this.realRequestDepth -= 1),
      () => (this.realRequestDepth -= 1)
    );
    return run;
  }

  private async doMint(opts?: MintOptions, isWarm = false): Promise<MintedSnapshot | null> {
    let raw = await this.deps.captureRaw(opts?.signal);
    if (!raw && !this.current && !opts?.signal?.aborted) {
      // First mint and capture was unavailable (page mid-navigation/attach is
      // common here). Retry once before giving up.
      raw = await this.deps.captureRaw(opts?.signal);
    }
    if (!raw) {
      // Couldn't capture (closed / aborted). Keep the current snapshot rather
      // than poison it. On the very first mint there's nothing to serve, so
      // return null and let the caller fall back to a legacy capture — never
      // throw, never serve stale.
      if (this.current) return this.current;
      this.deps.logger?.info?.(
        '[LiveSnapshot] captureRaw unavailable on first mint; caller falls back to legacy capture'
      );
      return null;
    }

    // Reuse the augmented snapshot when the raw substrate is byte-identical.
    if (
      !opts?.force &&
      this.current &&
      this.current.augmentedYaml !== null &&
      this.current.rawYaml === raw.rawYaml
    ) {
      return this.current;
    }

    // Freshness miss: the representation changed (or we were forced, or the
    // current snapshot isn't augmented yet).
    if (opts?.rawOnMiss) {
      // Hand back the raw substrate now; augment in the background.
      const snap = this.store(raw.url, raw.rawYaml, null);
      this.scheduleAugment(snap, opts?.signal);
      return snap;
    }

    return this.augmentAndStore(raw.url, raw.rawYaml, opts?.signal, isWarm);
  }

  /** Run the expensive augment tier, emit telemetry, and store the result. */
  private async augmentAndStore(
    url: string,
    rawYaml: string,
    signal: AbortSignal | undefined,
    isWarm: boolean
  ): Promise<MintedSnapshot> {
    const augStart = this.now();
    const augmentedYaml = await this.deps.augment(rawYaml, signal);
    this.deps.onEvent?.({ kind: 'augment', ms: this.now() - augStart });
    this.deps.onEvent?.({ kind: isWarm ? 'warm' : 'mint' });
    return this.store(url, rawYaml, augmentedYaml);
  }

  private store(url: string, rawYaml: string, augmentedYaml: string | null): MintedSnapshot {
    const snap: MintedSnapshot = {
      generation: ++this.generationCounter,
      url,
      rawYaml,
      augmentedYaml,
      capturedAtMs: this.now(),
    };
    this.current = snap;
    return snap;
  }

  /**
   * Background-augment a raw-only mint, upgrading `current` in place if still current.
   *
   * NOTE: only reachable on the opt-in `rawOnMiss` path, which is off in
   * production. TODO(rawOnMiss): before enabling, collapse a burst of queued
   * augments to the latest generation (track `pendingAugmentGen` and bail when
   * `this.current.generation` has advanced) so rapid misses don't serialize N
   * stale augments on the chain.
   */
  private scheduleAugment(snap: MintedSnapshot, signal?: AbortSignal): void {
    void this.enqueue(async () => {
      if (this.current !== snap) return; // page moved on; the new state will augment itself
      try {
        const augStart = this.now();
        const augmentedYaml = await this.deps.augment(snap.rawYaml, signal);
        this.deps.onEvent?.({ kind: 'augment', ms: this.now() - augStart });
        if (this.current === snap) {
          this.current = { ...snap, augmentedYaml };
        }
      } catch (err) {
        this.deps.logger?.debug?.('[LiveSnapshot] background augment failed (ignored)', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }).catch(() => undefined);
  }

  /**
   * Return the current AUGMENTED snapshot if its raw substrate still matches the
   * live page, else null — used to reuse the warm snapshot as a diff baseline.
   * Takes a fresh (cheap) raw substrate to verify; never augments.
   *
   * As a real-request entry point, this preempts an in-flight `warm` (same as
   * `mint`) so the warm yields instead of contending for the capture chain.
   */
  getReusable(opts?: { signal?: AbortSignal }): Promise<MintedSnapshot | null> {
    this.warmQuietAbort?.abort();
    return this.runReal(async () => {
      if (!this.current || this.current.augmentedYaml === null) return null;
      const raw = await this.deps.captureRaw(opts?.signal);
      if (!raw) return null;
      return raw.rawYaml === this.current.rawYaml ? this.current : null;
    });
  }

  /**
   * Fire-and-forget background refresh. Waits for the DOM to settle, then mints
   * (reusing or augmenting) so the next request hits the reuse path. Errors are
   * swallowed — warming must never surface to the agent.
   */
  warm(opts?: WarmOptions & { rawOnMiss?: boolean }): void {
    void this.warmAsync(opts).catch((err) => {
      this.deps.logger?.debug?.('[LiveSnapshot] warm failed (ignored)', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  }

  /**
   * Test seam: the awaitable warm body.
   *
   * Single-flight with real requests: the quiet-wait runs under a preemptible
   * signal so a `mint`/`getReusable` arriving mid-wait cancels it; the warm then
   * yields (a real request will do the capture+augment itself, so warming would
   * only duplicate work). Warm is idempotent — the next idle re-warms.
   */
  async warmAsync(opts?: WarmOptions & { rawOnMiss?: boolean }): Promise<void> {
    const quietAbort = new AbortController();
    this.warmQuietAbort = quietAbort;
    try {
      if (this.deps.waitQuiet) {
        // Race the quiet-wait against preemption so a real request cuts it short
        // instead of waiting out the full (up to 2s) window off-chain.
        await Promise.race([
          this.deps.waitQuiet(opts?.quietMs ?? 100, opts?.maxWaitMs ?? 2000),
          new Promise<void>((resolve) => {
            if (quietAbort.signal.aborted) return resolve();
            quietAbort.signal.addEventListener('abort', () => resolve(), { once: true });
          }),
        ]);
      }
    } finally {
      if (this.warmQuietAbort === quietAbort) this.warmQuietAbort = null;
    }
    // Yield to any real request: preempted mid-wait, or one that landed while we
    // waited. It owns the capture+augment; minting now would duplicate it.
    if (opts?.signal?.aborted || quietAbort.signal.aborted || this.realRequestDepth > 0) {
      this.deps.onEvent?.({ kind: 'warm-preempted' });
      return;
    }
    // Prepare unconditionally: an action can change the representation without a
    // DOM-observable signal, so we re-capture and re-augment on change rather
    // than trusting a proxy to say "nothing changed." `warm`/`mint` telemetry is
    // emitted inside the augment tier (only on an actual fresh augment).
    await this.enqueue(() => this.doMint({ signal: opts?.signal, rawOnMiss: opts?.rawOnMiss }, true));
  }
}
